import { cn } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';

interface BillingToggleProps {
  billingInterval: 'one-time' | 'month' | 'year' | 'week';
  setBillingInterval: (interval: 'one-time' | 'month' | 'year' | 'week') => void;
}

export function BillingToggle({ billingInterval, setBillingInterval }: BillingToggleProps) {
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState({ left: 0, width: 0 });

  useEffect(() => {
    const updatePill = () => {
      if (!tabContainerRef.current) return;
      const triggers = tabContainerRef.current.querySelectorAll<HTMLButtonElement>('button');
      const idx =
        billingInterval === 'year'
          ? 0
          : billingInterval === 'month'
            ? 1
            : billingInterval === 'week'
              ? 2
              : 3;
      const trigger = triggers[idx];
      if (trigger) {
        setPill({ left: trigger.offsetLeft, width: trigger.offsetWidth });
      }
    };

    const observer = new ResizeObserver(() => requestAnimationFrame(updatePill));
    if (tabContainerRef.current) observer.observe(tabContainerRef.current);

    const rafId1 = requestAnimationFrame(updatePill);
    const rafId2 = requestAnimationFrame(() => requestAnimationFrame(updatePill));

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId1);
      cancelAnimationFrame(rafId2);
    };
  }, [billingInterval]);

  return (
    <div className="mx-auto w-fit">
      <div
        ref={tabContainerRef}
        className="relative mx-auto flex w-fit rounded-full border border-zinc-600 bg-zinc-700 p-1.5"
      >
        <div
          className="absolute top-1.5 bottom-1.5 rounded-full bg-zinc-500 shadow-lg transition-all duration-300 ease-in-out"
          style={{
            left: pill.left || 0,
            width: pill.width || 0,
          }}
        />
        <button
          onClick={() => setBillingInterval('year')}
          className={cn(
            'relative z-10 flex w-24 cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-full py-1.5 text-[11px] transition-all sm:w-28 sm:text-[12px]',
            billingInterval === 'year'
              ? 'font-bold text-white shadow-sm'
              : 'font-medium text-zinc-300 hover:text-white'
          )}
        >
          Yearly{' '}
          <span
            className={cn(
              'font-bold text-[10px] sm:text-[11px]',
              billingInterval === 'year' ? 'text-indigo-200' : 'text-indigo-300'
            )}
          >
            -20%
          </span>
        </button>
        <button
          onClick={() => setBillingInterval('month')}
          className={cn(
            'relative z-10 w-24 cursor-pointer whitespace-nowrap rounded-full py-1.5 text-[11px] transition-all sm:w-28 sm:text-[12px]',
            billingInterval === 'month'
              ? 'font-bold text-white shadow-sm'
              : 'font-medium text-zinc-300 hover:text-white'
          )}
        >
          Monthly
        </button>
        <button
          onClick={() => setBillingInterval('week')}
          className={cn(
            'relative z-10 w-24 cursor-pointer whitespace-nowrap rounded-full py-1.5 text-[11px] transition-all sm:w-28 sm:text-[12px]',
            billingInterval === 'week'
              ? 'font-bold text-white shadow-sm'
              : 'font-medium text-zinc-300 hover:text-white'
          )}
        >
          Weekly
        </button>
        <button
          onClick={() => setBillingInterval('one-time')}
          className={cn(
            'relative z-10 w-24 cursor-pointer whitespace-nowrap rounded-full py-1.5 text-[11px] transition-all sm:w-28 sm:text-[12px]',
            billingInterval === 'one-time'
              ? 'font-bold text-white shadow-sm'
              : 'font-medium text-zinc-300 hover:text-white'
          )}
        >
          One-Time
        </button>
      </div>
    </div>
  );
}
